/**
 * The right-hand column of the ticket page: who owns it, its status and
 * priority, where it is filed, the vendor reference, the HR switch,
 * move-to-desk, and the confirmed-bug button. Every control saves on its own,
 * so there is no Save button for the whole rail.
 */

import { useEffect, useState } from 'react';
import type { SdPriority, SdStatus } from '@ampersandbrands/ui';
import { formatInstant } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import {
  useCategoriesAll,
  useConfirmBug,
  useDecideApproval,
  useDesks,
  useMoveTicket,
  usePatchTicket,
} from '@/lib/useServiceDesk';
import { SD_DESK } from '@/lib/serviceDeskClient';
import type { SdStaffTicketDetail } from '@/lib/serviceDeskTypes';
import { CategoryPicker } from './CategoryPicker';
import { PersonPicker } from './PersonPicker';
import { ReporterContact } from './ReporterContact';
import { SdError } from './SdShell';
import { activeOnly, findNode, pathLabel } from './categoryTree';
import {
  ALL_PRIORITIES,
  ALL_STATUSES,
  PRIORITY_LABELS,
  SLA_TONES,
  STATUS_LABELS,
  slaChipText,
} from './sdLabels';
import { SD_BTN_DANGER, SD_BTN_GHOST, SD_BTN_PRIMARY, SD_INPUT, SD_MUTED } from './sdStyles';

function Block({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-amp-line pb-3 last:border-b-0 dark:border-amp-dark-line">
      <FilterLabel htmlFor={htmlFor} as={htmlFor ? 'label' : 'div'}>
        {label}
      </FilterLabel>
      {children}
    </div>
  );
}

/**
 * HR approval on an Employee exit ticket. Everyone on the desk sees where it
 * stands; only a listed approver (or a super-admin) gets Approve and Decline.
 * Declining opens a reason box, because the requester is shown the reason.
 */
function ApprovalBlock({ ticket }: { ticket: SdStaffTicketDetail }) {
  const decide = useDecideApproval(ticket.id);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const a = ticket.approval;
  if (!a) return null;
  const d = a.decision;
  const names = a.approvers.map((p) => p.name ?? p.email).join(', ');
  return (
    <Block label="HR approval">
      {a.state === 'pending' && (
        <>
          <Pill tone="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">Waiting for HR</Pill>
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            {a.can_decide
              ? 'You are an approver for this request. No account is touched until it is approved.'
              : `Waiting for ${names || 'an HR approver'}. The status stays until one of them decides.`}
          </p>
        </>
      )}
      {a.state === 'approved' && d && (
        <>
          <Pill tone="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Approved</Pill>
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            By {d.by_name ?? d.by_email}, {formatInstant(d.decided_at)}.{' '}
            {a.offboard_ready_at
              ? 'Ready to offboard.'
              : a.offboard_on
                ? `Offboarding starts on ${a.offboard_on}.`
                : ''}
          </p>
        </>
      )}
      {a.state === 'declined' && d && (
        <>
          <Pill tone="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Declined</Pill>
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            By {d.by_name ?? d.by_email}, {formatInstant(d.decided_at)}.
            {d.reason ? ` Reason: ${d.reason}` : ''}
          </p>
        </>
      )}
      {a.can_decide && (
        <div className="mt-3">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'approve' })}
              className={SD_BTN_PRIMARY}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={decide.isPending}
              aria-expanded={declining}
              onClick={() => setDeclining((v) => !v)}
              className={SD_BTN_DANGER}
            >
              Decline
            </button>
          </div>
          {declining && (
            <div className="mt-2">
              <label htmlFor="sd-decline-reason" className={`mb-1 block text-[12px] ${SD_MUTED}`}>
                Why are you declining? The requester will see this.
              </label>
              <textarea
                id="sd-decline-reason"
                rows={3}
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={SD_INPUT}
              />
              <button
                type="button"
                disabled={decide.isPending || !reason.trim()}
                onClick={() =>
                  decide.mutate(
                    { decision: 'decline', reason: reason.trim() },
                    {
                      onSuccess: () => {
                        setDeclining(false);
                        setReason('');
                      },
                    },
                  )
                }
                className={`${SD_BTN_DANGER} mt-2`}
              >
                Decline this request
              </button>
            </div>
          )}
        </div>
      )}
      <SdError error={decide.error} what="record the decision" />
    </Block>
  );
}

function CategoryBlock({ ticket }: { ticket: SdStaffTicketDetail }) {
  const tree = useCategoriesAll();
  const patch = usePatchTicket(ticket.id);
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<number | null>(ticket.category_id);
  const active = activeOnly(tree.data ?? []);
  const node = findNode(active, choice);
  const isLeaf = !!node && node.children.length === 0;

  return (
    <Block label="Category">
      {!editing ? (
        <>
          <p className="text-[13px] text-amp-ink dark:text-amp-dark-ink">
            {pathLabel(tree.data ?? [], ticket.category_id) || ticket.category_path}
          </p>
          <button type="button" onClick={() => setEditing(true)} className={`${SD_BTN_GHOST} mt-2`}>
            Change category
          </button>
        </>
      ) : (
        <>
          <CategoryPicker idPrefix="sd-ticket-cat" tree={active} value={choice} onChange={(id) => setChoice(id)} />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={!isLeaf || patch.isPending}
              onClick={() => {
                if (choice !== null) patch.mutate({ category_id: choice }, { onSuccess: () => setEditing(false) });
              }}
              className={SD_BTN_GHOST}
            >
              Save
            </button>
            <button type="button" onClick={() => setEditing(false)} className={SD_BTN_GHOST}>
              Cancel
            </button>
          </div>
          {!isLeaf && <p className={`mt-1 text-[12px] ${SD_MUTED}`}>Keep choosing until the last level is filled in.</p>}
        </>
      )}
      <SdError error={patch.error} what="change the category" />
    </Block>
  );
}

function VendorBlock({ ticket }: { ticket: SdStaffTicketDetail }) {
  const patch = usePatchTicket(ticket.id);
  const [value, setValue] = useState(ticket.vendor_ticket_no ?? '');
  useEffect(() => setValue(ticket.vendor_ticket_no ?? ''), [ticket.vendor_ticket_no]);
  const dirty = value.trim() !== (ticket.vendor_ticket_no ?? '');
  return (
    <Block label="Vendor ticket number" htmlFor="sd-vendor-no">
      <div className="flex gap-2">
        <input
          id="sd-vendor-no"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={ticket.vendor ? `${ticket.vendor} reference` : 'Reference from the vendor'}
          className={SD_INPUT}
        />
        <button
          type="button"
          disabled={!dirty || patch.isPending}
          onClick={() => patch.mutate({ vendor_ticket_no: value.trim() || null })}
          className={SD_BTN_GHOST}
        >
          Save
        </button>
      </div>
    </Block>
  );
}

function MoveBlock({ ticket }: { ticket: SdStaffTicketDetail }) {
  const desks = useDesks();
  const move = useMoveTicket(ticket.id);
  const [to, setTo] = useState('');
  const current = ticket.desk_slug ?? SD_DESK;
  const others = (desks.data ?? []).filter((d) => d.slug !== current);
  if (others.length === 0) return null;
  return (
    <Block label="Move to another desk" htmlFor="sd-move">
      <div className="flex gap-2">
        <select id="sd-move" value={to} onChange={(e) => setTo(e.target.value)} className={SD_INPUT}>
          <option value="">Choose a desk</option>
          {others.map((d) => (
            <option key={d.slug} value={d.slug}>
              {d.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!to || move.isPending}
          onClick={() => {
            if (window.confirm('Move this ticket to the other desk? It gets a new ticket number there.')) move.mutate(to);
          }}
          className={SD_BTN_GHOST}
        >
          Move
        </button>
      </div>
      <SdError error={move.error} what="move the ticket" />
    </Block>
  );
}

export function TicketRail({ ticket }: { ticket: SdStaffTicketDetail }) {
  const { user } = useSession();
  const patch = usePatchTicket(ticket.id);
  const bug = useConfirmBug(ticket.id);

  const canConfirmBug =
    (ticket.source_app === 'cockpit' || ticket.source_app === 'sweetspot') && !ticket.confirmed_bug;

  return (
    <aside aria-label="Ticket details" className="flex flex-col gap-3 rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <ApprovalBlock ticket={ticket} />

      <Block label="Reporter">
        <p className="text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <ReporterContact name={ticket.reporter_name} email={ticket.reporter_email} fallback="Unknown" />
          {ticket.store_no ? <span className={SD_MUTED}>{` (store ${ticket.store_no})`}</span> : null}
        </p>
        <p className={`mt-0.5 text-[12px] ${SD_MUTED}`}>Hover the name for contact details.</p>
      </Block>

      <Block label="Assigned to">
        <p className="mb-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          {ticket.assignee_name ?? <span className={SD_MUTED}>Nobody yet</span>}
        </p>
        <div className="mb-2 flex flex-wrap gap-2">
          {user?.email && ticket.assignee_email !== user.email && (
            <button
              type="button"
              disabled={patch.isPending}
              onClick={() => patch.mutate({ assignee_email: user.email })}
              className={SD_BTN_GHOST}
            >
              Assign to me
            </button>
          )}
          {ticket.assignee_email && (
            <button
              type="button"
              disabled={patch.isPending}
              onClick={() => patch.mutate({ assignee_email: null })}
              className={SD_BTN_GHOST}
            >
              Unassign
            </button>
          )}
        </div>
        <PersonPicker
          placeholder="Assign to someone else"
          onPick={(p) => {
            if (p.email) patch.mutate({ assignee_email: p.email });
          }}
        />
      </Block>

      <Block label="Status" htmlFor="sd-status">
        <select
          id="sd-status"
          value={ticket.status}
          onChange={(e) => patch.mutate({ status: e.target.value as SdStatus })}
          disabled={ticket.approval?.state === 'pending'}
          className={SD_INPUT}
        >
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        {ticket.approval?.state === 'pending' && (
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>The status changes when HR decides.</p>
        )}
      </Block>

      <Block label="Priority" htmlFor="sd-priority">
        <select
          id="sd-priority"
          value={ticket.priority}
          onChange={(e) => patch.mutate({ priority: e.target.value as SdPriority })}
          className={SD_INPUT}
        >
          {ALL_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABELS[p]}
            </option>
          ))}
        </select>
      </Block>

      <Block label="Deadline">
        <Pill tone={SLA_TONES[ticket.sla_state]}>{slaChipText(ticket)}</Pill>
        {ticket.first_response_due_at && (
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>First reply by {formatInstant(ticket.first_response_due_at)}</p>
        )}
        {ticket.resolve_due_at && (
          <p className={`text-[12px] ${SD_MUTED}`}>Resolve by {formatInstant(ticket.resolve_due_at)}</p>
        )}
      </Block>

      <CategoryBlock ticket={ticket} />

      <VendorBlock ticket={ticket} />

      <Block label="HR restricted">
        <label className="flex cursor-pointer items-start gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input
            type="checkbox"
            checked={ticket.hr_restricted}
            onChange={(e) => patch.mutate({ hr_restricted: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            Only the requester, IT, and people mentioned on this ticket can open it.
          </span>
        </label>
      </Block>

      <MoveBlock ticket={ticket} />

      {(canConfirmBug || ticket.confirmed_bug) && (
        <Block label="Bug report">
          {ticket.confirmed_bug ? (
            <Pill tone="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Confirmed bug</Pill>
          ) : (
            <>
              <button type="button" disabled={bug.isPending} onClick={() => bug.mutate()} className={SD_BTN_DANGER}>
                Confirm this is a bug
              </button>
              <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
                Marks it for the developers. The requester is not told.
              </p>
            </>
          )}
          <SdError error={bug.error} what="confirm the bug" />
        </Block>
      )}

      <SdError error={patch.error} what="save that change" />
    </aside>
  );
}
